/**
 * Deciding whether a file is acceptable before a single byte of it is uploaded.
 *
 * The interesting half is the MP4 reader. Everything v1 has to know about a
 * video — its coded dimensions, its duration, whether it is H.264, and whether
 * it carries a rotation flag — lives in the `moov` box, which is a few kilobytes
 * near one end of the file. Reading it by hand costs a few hundred lines and
 * three ranged reads; the alternative, mediainfo.js, costs a multi-megabyte wasm
 * payload that either ships in the bundle or comes from a CDN the panel is not
 * allowed to talk to. So it is done here, over a `ByteSource` rather than a
 * `File`, which is also what makes the parser testable in Node with no DOM.
 *
 * The rejection copy is the product. A store manager who is told "unsupported
 * format" is stuck; one who is told which iPhone setting produces the file and
 * which setting to change is not. Every verdict below names the fix.
 */

import {
  ACCEPTED_IMAGE_MIME,
  ACCEPTED_VIDEO_CODECS,
  ACCEPTED_VIDEO_MIME,
  MAX_IMAGE_BYTES,
  MAX_VIDEO_BYTES,
  MAX_VIDEO_HEIGHT,
  MAX_VIDEO_WIDTH,
} from '@pbtv/shared';
import type { AssetType } from '@pbtv/shared';
import { formatBytes } from './format.js';

// ---------------------------------------------------------------------------
// Byte sources
// ---------------------------------------------------------------------------

/**
 * A random-access window onto bytes. A `Blob` gives one for free through
 * `slice()`, which is what keeps a 500 MB file out of memory: finding the `moov`
 * box reads a 16-byte header per top-level box and then the box itself.
 */
export interface ByteSource {
  readonly size: number;
  read(offset: number, length: number): Promise<Uint8Array>;
}

export function blobSource(blob: Blob): ByteSource {
  return {
    size: blob.size,
    async read(offset, length) {
      const clamped = Math.max(0, Math.min(length, blob.size - offset));
      if (clamped === 0) return new Uint8Array(0);
      return new Uint8Array(await blob.slice(offset, offset + clamped).arrayBuffer());
    },
  };
}

/** The same interface over an in-memory buffer, for tests and for small files. */
export function bufferSource(bytes: Uint8Array): ByteSource {
  return {
    size: bytes.length,
    read(offset, length) {
      const start = Math.max(0, Math.min(offset, bytes.length));
      const end = Math.max(start, Math.min(start + length, bytes.length));
      return Promise.resolve(bytes.subarray(start, end));
    },
  };
}

// ---------------------------------------------------------------------------
// ISO base media file format
// ---------------------------------------------------------------------------

export type Mp4ParseReason = 'not-mp4' | 'no-moov' | 'moov-too-large' | 'malformed';

export class Mp4ParseError extends Error {
  readonly reason: Mp4ParseReason;

  constructor(reason: Mp4ParseReason, message: string) {
    super(message);
    this.name = 'Mp4ParseError';
    this.reason = reason;
  }
}

/**
 * A `moov` box holds one sample table per track, so its size scales with the
 * frame count rather than the file size — a few megabytes for a long clip. The
 * cap is well clear of that and exists so a malformed header cannot ask the
 * browser to allocate the whole file.
 */
const MAX_MOOV_BYTES = 32 * 1024 * 1024;

interface Box {
  type: string;
  body: Uint8Array;
}

function viewOf(bytes: Uint8Array): DataView {
  return new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
}

function fourCc(bytes: Uint8Array, offset: number): string {
  let out = '';
  for (let index = 0; index < 4; index += 1) {
    const code = bytes[offset + index];
    if (code === undefined) return '';
    out += String.fromCharCode(code);
  }
  return out;
}

/**
 * Splits one box's payload into the boxes nested directly inside it.
 *
 * A `size` of 1 means the real length is a 64-bit field after the type; a `size`
 * of 0 means "to the end of the enclosing box". Both are rare in practice and
 * both appear in real files, so both are handled rather than treated as
 * corruption.
 */
function readBoxes(bytes: Uint8Array): Box[] {
  const view = viewOf(bytes);
  const out: Box[] = [];
  let offset = 0;

  while (offset + 8 <= bytes.length) {
    let size = view.getUint32(offset);
    const type = fourCc(bytes, offset + 4);
    let bodyStart = offset + 8;

    if (size === 1) {
      if (offset + 16 > bytes.length) break;
      const large = view.getBigUint64(offset + 8);
      if (large > BigInt(Number.MAX_SAFE_INTEGER)) break;
      size = Number(large);
      bodyStart = offset + 16;
    } else if (size === 0) {
      size = bytes.length - offset;
    }

    if (size < bodyStart - offset) break;
    const end = Math.min(offset + size, bytes.length);
    out.push({ type, body: bytes.subarray(bodyStart, end) });
    offset += size;
  }

  return out;
}

function findBox(boxes: readonly Box[], type: string): Box | null {
  for (const box of boxes) {
    if (box.type === type) return box;
  }
  return null;
}

interface TimedHeader {
  timescale: number;
  duration: number;
}

/** `mvhd` and `mdhd` share this prefix layout exactly; one reader serves both. */
function readTimedHeader(body: Uint8Array): TimedHeader | null {
  if (body.length < 4) return null;
  const view = viewOf(body);
  const version = body[0] ?? 0;

  if (version === 1) {
    if (body.length < 32) return null;
    return { timescale: view.getUint32(20), duration: Number(view.getBigUint64(24)) };
  }

  if (body.length < 20) return null;
  return { timescale: view.getUint32(12), duration: view.getUint32(16) };
}

function readHandler(body: Uint8Array): string | null {
  if (body.length < 12) return null;
  return fourCc(body, 8);
}

/**
 * The transform matrix a player applies to a track. Only the first two members
 * matter for rotation: the matrix is `[a b u; c d v; x y w]` in 16.16 fixed
 * point, and the four cardinal rotations are the four sign patterns of `a` and
 * `b`.
 */
function matrixRotationDegrees(view: DataView, offset: number): number {
  const a = view.getInt32(offset) / 65536;
  const b = view.getInt32(offset + 4) / 65536;
  if (a === 0 && b === 0) return 0;

  const degrees = Math.round((Math.atan2(b, a) * 180) / Math.PI);
  const normalised = ((degrees % 360) + 360) % 360;

  // Real files carry tiny rounding errors in the fixed-point members; a matrix
  // one degree off a right angle is a 90-degree rotation, not a shear.
  for (const cardinal of [0, 90, 180, 270, 360]) {
    if (Math.abs(normalised - cardinal) <= 1) return cardinal % 360;
  }
  return normalised;
}

interface TrackHeader {
  rotationDegrees: number;
  width: number;
  height: number;
}

function readTrackHeader(body: Uint8Array): TrackHeader | null {
  if (body.length < 4) return null;
  const view = viewOf(body);
  const version = body[0] ?? 0;
  const matrixOffset = version === 1 ? 52 : 40;
  if (body.length < matrixOffset + 44) return null;

  return {
    rotationDegrees: matrixRotationDegrees(view, matrixOffset),
    // The trailing width/height are 16.16 fixed point and describe the
    // presentation size, which is why they are only a fallback: the coded
    // dimensions in the sample entry are what a decoder actually has to handle.
    width: Math.round(view.getUint32(matrixOffset + 36) / 65536),
    height: Math.round(view.getUint32(matrixOffset + 40) / 65536),
  };
}

interface SampleFormat {
  format: string;
  width: number;
  height: number;
}

function readSampleFormat(body: Uint8Array): SampleFormat | null {
  if (body.length < 8) return null;
  const entries = readBoxes(body.subarray(8));
  const first = entries[0];
  if (first === undefined) return null;

  // VisualSampleEntry: six reserved bytes, a data reference index, then a run
  // of pre-defined fields, and the coded width and height at 24 and 26.
  if (first.body.length < 28) return { format: first.type.toLowerCase(), width: 0, height: 0 };
  const view = viewOf(first.body);
  return {
    format: first.type.toLowerCase(),
    width: view.getUint16(24),
    height: view.getUint16(26),
  };
}

export interface Mp4Track {
  /** `vide`, `soun`, `sbtl` and friends. */
  handler: string;
  /** The sample entry fourcc: `avc1` and `avc3` are H.264, `hvc1`/`hev1` HEVC. */
  format: string | null;
  width: number;
  height: number;
  durationMs: number | null;
  rotationDegrees: number;
}

export interface Mp4Metadata {
  durationMs: number | null;
  tracks: Mp4Track[];
}

function parseTrack(trak: Box): Mp4Track {
  const children = readBoxes(trak.body);
  const header = readTrackHeader(findBox(children, 'tkhd')?.body ?? new Uint8Array(0));

  const mdia = findBox(children, 'mdia');
  const mdiaChildren = mdia === null ? [] : readBoxes(mdia.body);
  const handler = readHandler(findBox(mdiaChildren, 'hdlr')?.body ?? new Uint8Array(0));
  const timing = readTimedHeader(findBox(mdiaChildren, 'mdhd')?.body ?? new Uint8Array(0));

  const minf = findBox(mdiaChildren, 'minf');
  const stbl = minf === null ? null : findBox(readBoxes(minf.body), 'stbl');
  const stsd = stbl === null ? null : findBox(readBoxes(stbl.body), 'stsd');
  const sample = stsd === null ? null : readSampleFormat(stsd.body);

  const width = sample !== null && sample.width > 0 ? sample.width : (header?.width ?? 0);
  const height = sample !== null && sample.height > 0 ? sample.height : (header?.height ?? 0);

  return {
    handler: handler ?? '',
    format: sample?.format ?? null,
    width,
    height,
    durationMs:
      timing === null || timing.timescale === 0 || timing.duration === 0
        ? null
        : Math.round((timing.duration / timing.timescale) * 1000),
    rotationDegrees: header?.rotationDegrees ?? 0,
  };
}

/** Pure: everything above the ranged reads, so it can be exercised byte by byte. */
export function parseMoov(body: Uint8Array): Mp4Metadata {
  const children = readBoxes(body);
  const movieHeader = readTimedHeader(findBox(children, 'mvhd')?.body ?? new Uint8Array(0));
  const tracks = children.filter((box) => box.type === 'trak').map(parseTrack);

  if (tracks.length === 0) {
    throw new Mp4ParseError('malformed', 'The movie box carries no tracks.');
  }

  const movieDurationMs =
    movieHeader === null || movieHeader.timescale === 0 || movieHeader.duration === 0
      ? null
      : Math.round((movieHeader.duration / movieHeader.timescale) * 1000);

  const trackDurationMs = tracks.reduce<number | null>(
    (longest, track) =>
      track.durationMs === null ? longest : Math.max(longest ?? 0, track.durationMs),
    null,
  );

  return { durationMs: movieDurationMs ?? trackDurationMs, tracks };
}

/**
 * Walks the top-level boxes until `moov` turns up.
 *
 * It is deliberately a scan rather than a peek at the head of the file: a clip
 * straight off a camera or a phone is not "faststart", so its `moov` sits after
 * a `mdat` that may be hundreds of megabytes. Skipping by the declared box size
 * finds it in three reads either way.
 */
export async function readMp4Metadata(source: ByteSource): Promise<Mp4Metadata> {
  let offset = 0;
  let sawKnownBox = false;

  while (offset + 8 <= source.size) {
    const head = await source.read(offset, Math.min(16, source.size - offset));
    if (head.length < 8) break;

    const view = viewOf(head);
    let size = view.getUint32(0);
    const type = fourCc(head, 4);
    let bodyStart = offset + 8;

    if (size === 1) {
      if (head.length < 16) break;
      const large = view.getBigUint64(8);
      if (large > BigInt(Number.MAX_SAFE_INTEGER)) break;
      size = Number(large);
      bodyStart = offset + 16;
    } else if (size === 0) {
      size = source.size - offset;
    }

    if (size < bodyStart - offset) {
      throw new Mp4ParseError('malformed', 'A box declares a length shorter than its own header.');
    }
    if (type === 'ftyp' || type === 'moov' || type === 'mdat' || type === 'free') {
      sawKnownBox = true;
    }

    if (type === 'moov') {
      const bodyLength = Math.min(offset + size, source.size) - bodyStart;
      if (bodyLength > MAX_MOOV_BYTES) {
        throw new Mp4ParseError('moov-too-large', 'The movie box is implausibly large.');
      }
      return parseMoov(await source.read(bodyStart, bodyLength));
    }

    offset += size;
  }

  throw new Mp4ParseError(
    sawKnownBox ? 'no-moov' : 'not-mp4',
    sawKnownBox ? 'The file has no movie box.' : 'The file is not an MP4.',
  );
}

// ---------------------------------------------------------------------------
// File types
// ---------------------------------------------------------------------------

const EXTENSION_TYPES: Record<string, string> = {
  mp4: 'video/mp4',
  m4v: 'video/mp4',
  mov: 'video/quicktime',
  qt: 'video/quicktime',
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  webp: 'image/webp',
  heic: 'image/heic',
  heif: 'image/heif',
  gif: 'image/gif',
  avi: 'video/x-msvideo',
  mkv: 'video/x-matroska',
  wmv: 'video/x-ms-wmv',
  webm: 'video/webm',
};

/**
 * The browser's own guess, with the extension as a fallback. Windows in
 * particular hands over an empty string or `application/octet-stream` for files
 * it has no association for, and a `.mov` that arrives untyped still deserves
 * the QuickTime answer rather than the generic one.
 */
export function resolveContentType(fileName: string, declaredType: string): string {
  const declared = declaredType.trim().toLowerCase();
  if (declared !== '' && declared !== 'application/octet-stream') return declared;

  const dot = fileName.lastIndexOf('.');
  if (dot < 0) return '';
  return EXTENSION_TYPES[fileName.slice(dot + 1).toLowerCase()] ?? '';
}

export type ContainerKind = 'mp4' | 'quicktime' | 'image' | 'heic' | 'unknown';

export function classifyContainer(contentType: string): ContainerKind {
  const type = contentType.toLowerCase();
  if ((ACCEPTED_VIDEO_MIME as readonly string[]).includes(type)) return 'mp4';
  if (type === 'video/quicktime') return 'quicktime';
  if ((ACCEPTED_IMAGE_MIME as readonly string[]).includes(type)) return 'image';
  if (type === 'image/heic' || type === 'image/heif') return 'heic';
  return 'unknown';
}

// ---------------------------------------------------------------------------
// Verdicts
// ---------------------------------------------------------------------------

export type MediaKind = 'video' | 'image';

/** Exactly the shape `uploadInitSchema.probe` expects. */
export interface MediaProbe {
  width: number;
  height: number;
  durationMs: number | null;
  videoCodec: string | null;
  rotationMetadataDegrees: number | null;
}

export type RejectionCode =
  | 'empty-file'
  | 'unsupported-type'
  | 'heic-image'
  | 'quicktime-container'
  | 'hevc-video'
  | 'unsupported-codec'
  | 'no-video-track'
  | 'video-too-large'
  | 'image-too-large'
  | 'too-many-pixels'
  | 'rotation-metadata'
  | 'unreadable';

export interface AcceptedFile {
  ok: true;
  kind: MediaKind;
  contentType: AssetType;
  probe: MediaProbe;
}

export interface RejectedFile {
  ok: false;
  code: RejectionCode;
  /** One line, shown in bold on the failed row. */
  title: string;
  /** What to change, in the words of the setting or the export dialog. */
  message: string;
}

export type FileVerdict = AcceptedFile | RejectedFile;

const IPHONE_VIDEO_FIX =
  'On an iPhone, open Settings → Camera → Formats and choose Most Compatible, then record the clip again. From an editor, export it as H.264 MP4.';

function reject(code: RejectionCode, title: string, message: string): RejectedFile {
  return { ok: false, code, title, message };
}

function isH264(format: string): boolean {
  return (ACCEPTED_VIDEO_CODECS as readonly string[]).includes(format);
}

function isHevc(format: string): boolean {
  return format === 'hvc1' || format === 'hev1' || format === 'hvc2' || format === 'dvh1';
}

/**
 * The v1 pixel ceiling, read as a budget rather than as a width and a height.
 *
 * A portrait screen is served by a pre-rotated 1080×1920 file — that is the
 * documented way to get portrait video onto a TV at all, since the rotation flag
 * is refused — so measuring the long edge against 1920 and the short edge
 * against 1080 is the only reading that lets the documented workflow through.
 * The constraint being enforced is decoder capability, which does not care which
 * way up the frame is.
 */
export function exceedsPixelBudget(width: number, height: number): boolean {
  const longEdge = Math.max(width, height);
  const shortEdge = Math.min(width, height);
  return longEdge > MAX_VIDEO_WIDTH || shortEdge > MAX_VIDEO_HEIGHT;
}

export function videoTooLarge(bytes: number): RejectedFile {
  return reject(
    'video-too-large',
    `This video is ${formatBytes(bytes)}, over the ${formatBytes(MAX_VIDEO_BYTES)} limit`,
    'Shorten the clip or export it at a lower bitrate — 8 Mbps is generous for 1080p signage. Every screen downloads every file it is given, and a stick has about five gigabytes to work with.',
  );
}

export function imageTooLarge(bytes: number): RejectedFile {
  return reject(
    'image-too-large',
    `This image is ${formatBytes(bytes)}, over the ${formatBytes(MAX_IMAGE_BYTES)} limit`,
    'Export it at 1920 × 1080 and around 80% JPEG quality. A still that fills a TV rarely needs more than a couple of megabytes.',
  );
}

export interface VideoJudgement {
  bytes: number;
  container: 'mp4' | 'quicktime';
  metadata: Mp4Metadata;
}

/**
 * Pure: metadata in, verdict out.
 *
 * The order of the checks is the order in which the answers are useful. Codec
 * comes first because an HEVC `.mov` off an iPhone is both wrong-codec and
 * wrong-container, and the one instruction that fixes it is the codec one.
 */
export function judgeVideo({ bytes, container, metadata }: VideoJudgement): FileVerdict {
  const video = metadata.tracks.find((track) => track.handler === 'vide') ?? null;

  if (video === null || video.format === null) {
    return reject(
      'no-video-track',
      'This file has no video in it',
      'The container was read successfully but carries no video track — it may be an audio-only recording. Upload the clip itself.',
    );
  }

  if (isHevc(video.format)) {
    return reject(
      'hevc-video',
      'This video is HEVC, which the TVs cannot play',
      `HEVC — Apple calls it High Efficiency — is what an iPhone records by default, and neither the Fire TV sticks nor this preview can decode it. ${IPHONE_VIDEO_FIX}`,
    );
  }

  if (!isH264(video.format)) {
    return reject(
      'unsupported-codec',
      `This video is encoded as ${video.format}, and v1 plays H.264 only`,
      `Re-export the clip as H.264 MP4 — in Handbrake or Premiere that is the "H.264" video codec, and in QuickTime it is any of the preset MP4 exports. ${IPHONE_VIDEO_FIX}`,
    );
  }

  if (container === 'quicktime') {
    return reject(
      'quicktime-container',
      'This is a MOV file, and the library takes MP4',
      `The video inside it is already H.264, so only the container is wrong. Export or convert it to .mp4 — most editors offer "MP4" directly in the export dialog. ${IPHONE_VIDEO_FIX}`,
    );
  }

  if (video.width <= 0 || video.height <= 0) {
    return reject(
      'unreadable',
      'The video dimensions could not be read',
      'The file may be truncated or still copying. Try the file again once the transfer has finished, or re-export it.',
    );
  }

  if (exceedsPixelBudget(video.width, video.height)) {
    return reject(
      'too-many-pixels',
      `This clip is ${video.width} × ${video.height}, and v1 tops out at ${MAX_VIDEO_WIDTH} × ${MAX_VIDEO_HEIGHT}`,
      `A Fire TV Stick decodes 4K unreliably in a looping player, and nothing on a shop wall shows more than 1080p. Export at ${MAX_VIDEO_WIDTH} × ${MAX_VIDEO_HEIGHT}, or ${MAX_VIDEO_HEIGHT} × ${MAX_VIDEO_WIDTH} for a screen that has been turned on its side.`,
    );
  }

  if (video.rotationDegrees !== 0) {
    return reject(
      'rotation-metadata',
      `This video is flagged to be rotated ${video.rotationDegrees}° on playback`,
      'The TV applies that flag in its own decoder, on top of the rotation the app does for a sideways screen, and the picture ends up wrong with no way to fix it from the panel. Export the clip with the pixels already the right way up and the rotation flag cleared — in Handbrake that is the Rotate filter set to 0 after rotating, and in Premiere it is a sequence whose frame size matches the finished orientation.',
    );
  }

  if (bytes > MAX_VIDEO_BYTES) return videoTooLarge(bytes);

  return {
    ok: true,
    kind: 'video',
    contentType: 'video/mp4',
    probe: {
      width: video.width,
      height: video.height,
      durationMs: metadata.durationMs ?? video.durationMs,
      videoCodec: video.format,
      rotationMetadataDegrees: video.rotationDegrees,
    },
  };
}

export interface ImageJudgement {
  bytes: number;
  contentType: string;
  width: number;
  height: number;
}

export function judgeImage({ bytes, contentType, width, height }: ImageJudgement): FileVerdict {
  if (bytes > MAX_IMAGE_BYTES) return imageTooLarge(bytes);

  if (width <= 0 || height <= 0) {
    return reject(
      'unreadable',
      'This image could not be decoded',
      'The file may be truncated, or it may carry an extension that does not match what is inside it. Open it, re-save it as JPEG or PNG, and try again.',
    );
  }

  return {
    ok: true,
    kind: 'image',
    contentType: contentType as AssetType,
    probe: {
      width,
      height,
      durationMs: null,
      videoCodec: null,
      rotationMetadataDegrees: null,
    },
  };
}

/** The verdicts that need no decoding at all: wrong container, or nothing to read. */
export function judgeContainer(bytes: number, container: ContainerKind): RejectedFile | null {
  if (bytes === 0) {
    return reject(
      'empty-file',
      'This file is empty',
      'Nothing was read from it. A folder cannot be dropped as a whole — open it and drop what is inside. If the file lives on a network drive or is still syncing from a phone, wait for the copy to finish and try again.',
    );
  }

  if (container === 'heic') {
    return reject(
      'heic-image',
      'This photo is HEIC, which the TVs cannot display',
      'It is the same iPhone setting as video: Settings → Camera → Formats → Most Compatible makes the camera shoot JPEG. For a photo you already have, share it to Files or Mail and choose the JPEG option, or export it as JPEG from Photos on a Mac.',
    );
  }

  if (container === 'unknown') {
    return reject(
      'unsupported-type',
      'This kind of file cannot go on a TV',
      'The library takes H.264 MP4 video and JPEG, PNG or WebP images. PDFs, PowerPoint decks and GIFs have to be exported to one of those first.',
    );
  }

  return null;
}

// ---------------------------------------------------------------------------
// Browser entry point
// ---------------------------------------------------------------------------

async function imageDimensions(file: Blob): Promise<{ width: number; height: number }> {
  if (typeof createImageBitmap === 'function') {
    const bitmap = await createImageBitmap(file);
    const size = { width: bitmap.width, height: bitmap.height };
    bitmap.close();
    return size;
  }

  // Safari only grew `createImageBitmap` for blobs late; an <img> is the
  // fallback that has always worked.
  const url = URL.createObjectURL(file);
  try {
    return await new Promise<{ width: number; height: number }>((resolve, reject_) => {
      const image = new Image();
      image.onload = () => resolve({ width: image.naturalWidth, height: image.naturalHeight });
      image.onerror = () => reject_(new Error('The browser could not decode this image.'));
      image.src = url;
    });
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * The whole check, for one file the person just dropped on the page.
 *
 * It never throws for a bad file — a rejection is an answer, not an error — and
 * only propagates something unexpected if the browser itself misbehaves.
 */
async function probeImageFile(file: File, contentType: string): Promise<FileVerdict> {
  // Checked before the decode, so an oversized still is refused without asking
  // the browser to rasterise it first.
  if (file.size > MAX_IMAGE_BYTES) return imageTooLarge(file.size);

  try {
    const { width, height } = await imageDimensions(file);
    return judgeImage({ bytes: file.size, contentType, width, height });
  } catch {
    return judgeImage({ bytes: file.size, contentType, width: 0, height: 0 });
  }
}

async function probeVideoFile(
  file: File,
  container: 'mp4' | 'quicktime',
): Promise<FileVerdict> {
  try {
    const metadata = await readMp4Metadata(blobSource(file));
    return judgeVideo({ bytes: file.size, container, metadata });
  } catch (cause) {
    if (file.size > MAX_VIDEO_BYTES) return videoTooLarge(file.size);
    const detail =
      cause instanceof Mp4ParseError && cause.reason === 'not-mp4'
        ? 'The extension says MP4 but the contents are something else.'
        : 'The file may be truncated, still copying, or written by a tool this reader does not understand.';
    return reject(
      'unreadable',
      'This video could not be read',
      `${detail} Re-export it as H.264 MP4 and try again.`,
    );
  }
}

export async function probeFile(file: File): Promise<FileVerdict> {
  const contentType = resolveContentType(file.name, file.type);
  const container = classifyContainer(contentType);

  const early = judgeContainer(file.size, container);
  if (early !== null) return early;

  switch (container) {
    case 'image':
      return probeImageFile(file, contentType);
    case 'mp4':
    case 'quicktime':
      return probeVideoFile(file, container);
    default:
      // `judgeContainer` already refuses every other kind; this keeps the
      // exhaustiveness honest rather than relying on it from a distance.
      return reject(
        'unsupported-type',
        'This kind of file cannot go on a TV',
        'The library takes H.264 MP4 video and JPEG, PNG or WebP images.',
      );
  }
}
